import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import LegoAdminPageContext from '../../../pages/legoAdminPageContext';
import SingleTextEditor from '../../common/editors/SingleTextEditor';
import { useMounted } from '../../ai/AskAI';
import AIModelSelector from '../../../pages/cerokm/components/AIModelSelector';

function buildPrompt(query, boxes) {
  const fuseTable = boxes.map((box, bi) => {
    const header = `## Box ${bi}: ${box.boxName || 'Unnamed'}`;
    const rows = (box.fuses || [])
      .filter(f => f.id || f.description)
      .map(f => {
        const kind = f.kind ? `[${f.kind.type || ''}${f.kind.amp ? ' ' + f.kind.amp + 'A' : ''}]` : '';
        const entities = (f.relatedEntities || []).join(', ');
        return `  ${f.id || '?'} ${kind} ${f.description || ''} ${entities ? `(${entities})` : ''}`;
      });
    return [header, ...rows].join('\n');
  }).join('\n\n');

  return `You are a car fusebox search assistant. The user is looking for a fuse or relay in a car's fusebox.

USER QUERY: "${query}"

COMPLETE FUSEBOX DATA:
${fuseTable}

Search all boxes and fuses for the best matches to the user's query. Consider fuse IDs, descriptions, and relatedEntities (semantic tags). If there are clear matches, don't guess.
Return a JSON array of the top matches (up to 10), ordered by relevance. Each entry:
{ "boxIndex": 0-based box number, "fuseId": "fuse id string", "score": 1-100 relevance score, "reason": "brief reason for match" }

Return ONLY the JSON array.`;
}

export default function FuseLLMSearch({ boxes }) {
  const { page } = useContext(LegoAdminPageContext);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [rawText, setRawText] = useState('');
  const [model, setModel] = useState('gemini-flash-lite-latest');
  const [activePromptId, setActivePromptId] = useState(null);
  const activePromptIdRef = useRef(null);
  const isMounted = useMounted();

  const streamingService = 'services/llm/prompt-streaming';

  // Listen for streaming progress
  useEffect(() => {
    let myId = activePromptIdRef.current;
    let partialOutput = '';

    const onProgress = ({ promptId, newOutput, stats }) => {
      if (myId !== promptId || !isMounted()) return;
      if (stats) return;
      partialOutput += newOutput;
      setRawText(partialOutput);
    };

    if (myId) {
      page.service(streamingService).on('prompt-progress', onProgress);
    }

    return () => {
      if (myId) {
        page.service(streamingService).removeListener('prompt-progress', onProgress);
      }
    };
  }, [activePromptId, isMounted, page]);

  const search = useCallback(async () => {
    if (!query.trim() || loading) return;
    setLoading(true);
    setResults(null);
    setRawText('');

    const prompt = buildPrompt(query, boxes);
    const promptId = Math.ceil(Math.random() * 100000);
    setActivePromptId(promptId);
    activePromptIdRef.current = promptId;

    try {
      const res = await page.service(streamingService).create({
        prompt,
        promptId,
        model,
      });

      if (!isMounted()) return;

      // Parse the final response
      const text = typeof res === 'string' ? res : JSON.stringify(res);
      const cleaned = text.replace(/^```json\s*|```\s*$/g, '').trim();
      try {
        const parsed = JSON.parse(cleaned);
        setResults(enrichResults(parsed, boxes));
      } catch {
        setRawText(text);
      }
    } catch (err) {
      if (isMounted()) setRawText(`Error: ${err.message}`);
    } finally {
      if (isMounted()) {
        setLoading(false);
        activePromptIdRef.current = null;
        setActivePromptId(null);
      }
    }
  }, [query, boxes, loading, page, isMounted, model]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') search();
  }, [search]);

  return (
    <div style={{ minWidth: '600px' }}>
      <div className="d-flex align-items-center mb-3">
        <SingleTextEditor value={query} onChange={setQuery} placeholder="Search fuses" className="flex-grow-1" onKeyDown={handleKeyDown} autoFocus />

        <button className="btn btn-primary ml-2 no-wrap" style={{ minWidth: '120px' }} onClick={search} disabled={loading || !query.trim()} >
          {loading ? <><span className="spinner-border spinner-border-sm mr-1" role="status" /> Searching...</> : 'Search'}
        </button>

        <div style={{ width: '300px' }}>
        <AIModelSelector 
            value={model}
            onChange={setModel}
          />
        </div>
      </div>

      {results && results.length > 0 && (
        <table className="table table-sm table-striped table-bordered">
          <thead className="thead-dark">
            <tr>
              <th>Score</th>
              <th>Box</th>
              <th>Fuse ID</th>
              <th>Description</th>
              <th>Semantics</th>
              <th>Reason</th>
            </tr>
          </thead>
          <tbody>
            {results.map((r, i) => (
              <tr key={i}>
                <td><span className={`badge badge-${r.score >= 70 ? 'success' : r.score >= 40 ? 'warning' : 'secondary'}`}>{r.score}</span></td>
                <td className="text-nowrap">{r.boxName || `Box ${r.boxIndex + 1}`}</td>
                <td><strong>{r.fuseId}</strong></td>
                <td>{r.description || ''}</td>
                <td className="small text-muted">{r.entities || ''}</td>
                <td className="small">{r.reason || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {results && results.length === 0 && (
        <div className="alert alert-warning">No matches found.</div>
      )}

      {!results && rawText && (
        <pre className="bg-light p-2 rounded small" style={{ whiteSpace: 'pre-wrap' }}>
          {rawText}
          {loading && <span className="spinner-border spinner-border-sm ml-1" role="status" />}
        </pre>
      )}

      {loading && !rawText && !results && (
        <div className="text-muted text-center py-3">
          <span className="spinner-border spinner-border-sm mr-1" role="status" />
          Searching with LLM...
        </div>
      )}
    </div>
  );
}

function enrichResults(parsed, boxes) {
  if (!Array.isArray(parsed)) return [];
  return parsed.map(r => {
    const box = boxes[r.boxIndex];
    const fuse = box?.fuses?.find(f => f.id === r.fuseId);
    return {
      ...r,
      boxName: box?.boxName,
      description: fuse?.description || '',
      entities: (fuse?.relatedEntities || []).join(', '),
    };
  });
}
